import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export type OrgNavMode = 'community' | 'organization';

const STORAGE_KEY = 'jamaah_org_nav_mode';

function readStoredMode(): OrgNavMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'community' || stored === 'organization') return stored;
  } catch {}
  return 'organization';
}

interface OrgNavConfig {
  activeKey: string;
  enabledKeys: Set<string>;
  onSelect: (key: string) => void;
}

interface OrgNavModeContextType {
  navMode: OrgNavMode;
  setNavMode: (mode: OrgNavMode) => void;
  /** Org type key of the currently viewed org — null when not on a typed org page */
  orgTypeKey: string | null;
  setOrgTypeKey: (key: string | null) => void;
  /** Org-specific nav config (activeKey, enabledKeys, onSelect) — set by the profile layout */
  orgNavConfig: OrgNavConfig | null;
  setOrgNavConfig: (config: OrgNavConfig | null) => void;
}

const OrgNavModeContext = createContext<OrgNavModeContextType | undefined>(undefined);

export function OrgNavModeProvider({ children }: { children: ReactNode }) {
  const [navMode, setNavModeState] = useState<OrgNavMode>(readStoredMode);
  const [orgTypeKey, setOrgTypeKey] = useState<string | null>(null);
  const [orgNavConfig, setOrgNavConfig] = useState<OrgNavConfig | null>(null);

  const setNavMode = useCallback((mode: OrgNavMode) => {
    setNavModeState(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }, []);

  return (
    <OrgNavModeContext.Provider value={{ navMode, setNavMode, orgTypeKey, setOrgTypeKey, orgNavConfig, setOrgNavConfig }}>
      {children}
    </OrgNavModeContext.Provider>
  );
}

export function useOrgNavMode(): OrgNavModeContextType {
  const context = useContext(OrgNavModeContext);
  if (!context) throw new Error('useOrgNavMode must be used within OrgNavModeProvider');
  return context;
}



